// src/pages/Settings.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  Switch,
  Alert,
  Snackbar,
  Container,
  Stack,
  CircularProgress,
  List,
  ListItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { Settings as SettingsLucide } from 'lucide-react';
import api from '../services/api';
import ClockAccessOverrides from './ClockAccessOverrides';

// ─── SettingsSection ──────────────────────────────────────────────────────────
const SettingsSection = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="caption"
      sx={{
        color: '#8e8e93',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        px: 2,
        mb: 1,
        display: 'block'
      }}
    >
      {title}
    </Typography>
    <Card
      elevation={0}
      sx={{ bgcolor: '#ffffff', borderRadius: 3, border: 'none', boxShadow: 'none' }}
    >
      <List sx={{ p: 0 }}>
        {children}
      </List>
    </Card>
  </Box>
);

// ─── SettingsItem ─────────────────────────────────────────────────────────────
const SettingsItem = ({ icon: Icon, title, subtitle, value, onChange, iconColor = '#007AFF', hasText, onTextClick }) => (
  <ListItem sx={{
    py: 2,
    px: 2,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    position: 'relative'
  }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 2,
      bgcolor: iconColor,
      color: 'white',
      flexShrink: 0,
      mt: 0.5
    }}>
      <Icon sx={{ fontSize: 20 }} />
    </Box>

    <Box sx={{ flex: 1, minWidth: 0, pr: (onChange && hasText) ? 15 : (onChange || hasText) ? 9 : 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000', lineHeight: 1.4, mb: subtitle ? 0.5 : 0 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: '#8e8e93', lineHeight: 1.3, wordBreak: 'break-word' }}>
          {subtitle}
        </Typography>
      )}
    </Box>

    {/* Right-side controls */}
    <Box sx={{
      position: 'absolute',
      right: 12,
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 0.25
    }}>
      {hasText && (
        <IconButton
          size="small"
          onClick={onTextClick}
          title="Edit text note"
          sx={{
            color: '#c7c7cc',
            '&:hover': { color: '#007AFF', bgcolor: '#f0f6ff' },
            borderRadius: 1.5,
            p: 0.5
          }}
        >
          <NotesIcon sx={{ fontSize: 19 }} />
        </IconButton>
      )}
      {onChange && (
        <Switch
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#34c759' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#34c759' },
            '& .MuiSwitch-track': { backgroundColor: '#e5e5ea' },
          }}
        />
      )}
    </Box>
  </ListItem>
);

// ─── TextModal ────────────────────────────────────────────────────────────────
const TextModal = ({ open, onClose, rowKey, label, value, onSave }) => {
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    if (open) setDraft(value ?? '');
  }, [open, value]);

  const handleSave = () => {
    onSave(rowKey, draft);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
        {label}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          autoFocus
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          variant="outlined"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Enter text…"
          sx={{
            mt: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': { borderColor: '#007AFF' }
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: '#8e8e93', borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ bgcolor: '#007AFF', borderRadius: 2, '&:hover': { bgcolor: '#0062cc' } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function humanizeKey(key) {
  if (!key) return '';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Settings page ────────────────────────────────────────────────────────────
const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [settingsRows, setSettingsRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState(null);

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      try {
        const resp = await api.get('/api/admin-settings/rows');
        const rows = Array.isArray(resp.data) ? resp.data : [];
        setSettingsRows(rows);
        return;
      } catch (e) {
        console.log('⚠️ /api/admin-settings/rows não existe ainda, usando fallback...');
      }

      let response;
      try {
        response = await api.get('/api/admin-settings');
      } catch {
        response = await api.get('/api/clock-settings');
      }

      const data = response.data || {};
      const rows = Object.keys(data).sort().map((k) => ({
        id: null, key: k, value: data[k],
        text_value: undefined, description: '', category: 'General', updated_at: null
      }));
      setSettingsRows(rows);
    } catch (error) {
      showSnackbar('Erro ao carregar configurações', 'error');
      setSettingsRows([]);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const saveSetting = useCallback(async (key, value) => {
    try {
      setSettingsRows(prev => prev.map(r => r.key === key ? { ...r, value } : r));
      await api.put(`/api/admin-settings/${key}`, { value });
      showSnackbar('Setting updated!', 'success');
    } catch {
      showSnackbar('Error saving setting', 'error');
      loadSettings();
    }
  }, [loadSettings, showSnackbar]);

  const saveTextSetting = useCallback(async (key, text_value) => {
    try {
      setSettingsRows(prev => prev.map(r => r.key === key ? { ...r, text_value } : r));
      await api.put(`/api/admin-settings/${key}`, { text_value });
      showSnackbar('Setting updated!', 'success');
    } catch {
      showSnackbar('Error saving setting', 'error');
      loadSettings();
    }
  }, [loadSettings, showSnackbar]);

  const openTextModal = useCallback((row) => {
    setModalRow({
      key: row.key,
      label: row.description?.trim() ? row.description : humanizeKey(row.key),
      text_value: row.text_value
    });
    setModalOpen(true);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of settingsRows) {
      const cat = row.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(row);
    }
    return Array.from(map.entries()).map(([category, rows]) => ({
      category,
      rows: rows.slice().sort((a, b) => String(a.key).localeCompare(String(b.key)))
    }));
  }, [settingsRows]);

  const catMeta = useCallback((category) => {
    const c = String(category || '').toLowerCase();
    if (c.includes('clock')) return { Icon: ReceiptIcon, color: '#ff9500' };
    if (c.includes('print')) return { Icon: PrintIcon, color: '#3b82f6' };
    return { Icon: PrintIcon, color: '#8e8e93' };
  }, []);

  if (loading) {
    return (
      <Box sx={{
        position: 'fixed', top: '64px', left: 0, right: 0, bottom: 0,
        bgcolor: '#f2f2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} sx={{ color: '#007AFF' }} />
          <Typography variant="h6" sx={{ color: '#8e8e93' }}>Loading Settings...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      bgcolor: '#f2f2f7', overflow: 'auto', WebkitOverflowScrolling: 'touch'
    }}>
      <Container
        maxWidth="md"
        sx={{
          px: { xs: 2, md: 4 }, py: { xs: 2, md: 4 },
          pb: { xs: 8, md: 4 }, minHeight: '100%', display: 'flex', flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, pt: '80px' }}>
          <Typography variant="h4" sx={{
            fontWeight: 700, color: '#1e293b', mb: 1,
            display: 'flex', alignItems: 'center', gap: 1.5,
            fontSize: { xs: '1.5rem', md: '2.125rem' }
          }}>
            <SettingsLucide size={32} color="#3b82f6" />
            Settings
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: { xs: '0.875rem', md: '1rem' } }}>
            Manage your settings and preferences
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1 }}>
          {grouped.length === 0 ? (
            <Card elevation={0} sx={{ p: 2, borderRadius: 3 }}>
              <Typography sx={{ color: '#64748b' }}>No settings found.</Typography>
            </Card>
          ) : (
            grouped.map((group) => (
              <SettingsSection key={group.category} title={group.category}>
                {group.rows.map((row, i) => {
                  const { Icon, color } = catMeta(group.category);
                  const isBool  = typeof row.value === 'boolean';
                  const hasText = row.text_value !== undefined;
                  const label    = row.description?.trim() ? humanizeKey(row.key) : '';
                  const sublabel = row.description?.trim() ? row.description : humanizeKey(row.key);

                  return (
                    <React.Fragment key={row.key}>
                      <SettingsItem
                        icon={Icon}
                        title={label}
                        subtitle={sublabel}
                        value={row.value}
                        onChange={isBool ? (v) => saveSetting(row.key, v) : null}
                        iconColor={color}
                        hasText={hasText}
                        onTextClick={() => openTextModal(row)}
                      />
                      {i !== group.rows.length - 1 && (
                        <Divider variant="inset" sx={{ ml: 6 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </SettingsSection>
            ))
          )}

          {/* Liberação de clock por funcionária + data (tabela própria, não é
              uma linha de settings) */}
          <Box sx={{ mt: 3 }}>
            <ClockAccessOverrides />
          </Box>
        </Box>
      </Container>

      {/* Text modal */}
      {modalRow && (
        <TextModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          rowKey={modalRow.key}
          label={modalRow.label}
          value={modalRow.text_value}
          onSave={saveTextSetting}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 24, md: 24 } }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%', borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            maxWidth: { xs: 'calc(100vw - 32px)', md: 'none' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;